import { EntityType, EntityTypeEnum, SubscriptionIdentifier } from 'api'
import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { createAllEntitiesSelector, createEntityGetterSelector, createEntitySelector } from 'selectors/entitySelectors'
import { createEntitySubscriptionSelector } from 'selectors/selectionSelectors'
import { createEntitySnapshotLoadedSelector } from 'selectors/systemSelectors'
import { identityPropsSelector, useParametricSelector } from 'utils'

/**
 * Hook to get all entities within an entity type.
 *
 * Example:
 *
 * ```const allOrganisations = useAllEntities(EntityType.ORGANISATION)```
 */
export const useAllEntities = <TEntityType extends EntityType>(entityType: TEntityType) => {
  const [selector] = useState(() => createAllEntitiesSelector(entityType))
  const allEntities = useSelector(selector)

  return allEntities
}

/**
 * Hook to create an entity getter. The preference should
 * be a custom entity hook such as useOrganisation as it makes the
 * components cleaner but sometimes due to usage inside array methods
 * like map, onEvents or conditional usage is needed.
 *
 * Example:
 *
 * ```const getOrganisation = useEntityGetter(EntityType.ORGANISATION)```
 * ```const organisation = getOrganisation(id)```
 */
export const useEntityGetter = <TEntityType extends EntityType>(entityType: TEntityType) => {
  const [selector] = useState(() => createEntityGetterSelector(entityType))
  const getEntity = useSelector(selector)

  return getEntity
}

export const useIsSnapshotLoaded = <TEntityType extends EntityTypeEnum>(entityType: TEntityType) => {
  const selector = useMemo(() => createEntitySnapshotLoadedSelector(entityType), [entityType])
  return useSelector(selector)
}

export const useSubscription = <TEntityType extends EntityTypeEnum, TSubscriptionIdentifier extends SubscriptionIdentifier>(
  entityType: TEntityType,
  subscriptionIdentifier: TSubscriptionIdentifier
) => {
  const selector = useMemo(
    () =>
      createEntitySubscriptionSelector(
        () => entityType,
        () => subscriptionIdentifier
      ),
    [entityType, subscriptionIdentifier]
  )

  return useSelector(selector)
}

///// Factory Hooks

/**
 * Factory function to create a useAllEntity hook. Better to use within this module
 * to create hooks on organisation.ts, tradingEntity.ts etc
 *
 * Example:
 *
 * ```// on hooks/entity/organisation.ts```
 * ```const useAllOrganisations = createAllEntitiesHook(EntityType.ORGANISATION)```
 * ```// later on inside of a component```
 * ```const allOrganisations = useAllOrganisations()```
 */
export const createAllEntitiesHook = <TEntityType extends EntityType>(entityType: TEntityType) => {
  const selector = createAllEntitiesSelector(entityType)

  return function useAllEntities() {
    return useSelector(selector)
  }
}

/**
 * Factory function to create a useEntityGetter hook. Better to use within this module
 * to create hooks on organisation.ts, tradingEntity.ts etc
 *
 * Example:
 *
 * ```// on hooks/entity/organisation.ts```
 * ```const useOrganisationGetter = createEntityHook(EntityType.ORGANISATION)```
 * ```// later on inside of a component```
 * ```const getOrganisation = useOrganisationGetter()```
 */
export const createEntityGetterHook = <TEntityType extends EntityType>(entityType: TEntityType) => {
  const selector = createEntityGetterSelector(entityType)

  return function useEntityGetter() {
    return useSelector(selector)
  }
}

/**
 * Factory function to create a useEntity hook. Better to use within this module
 * to create hooks on organisation.ts, tradingEntity.ts etc
 *
 * Example:
 *
 * ```// on hooks/entity/organisation.ts```
 * ```const useOrganisation = createEntityHook(EntityType.ORGANISATION)```
 * ```// later on inside of a component```
 * ```const organisation = useOrganisation(id)```
 */
export const createEntityHook = <TEntityType extends EntityType>(entityType: TEntityType) => {
  const selector = createEntitySelector(identityPropsSelector, entityType)

  return function useEntity<T extends string>(entityId: T | null | undefined) {
    return useParametricSelector(selector, entityId)
  }
}
